/**
 * Sign In Page
 * 
 * Multi-auth sign in with:
 * - GitHub OAuth
 * - Google OAuth
 * - Email/Password with verification
 * - Password reset flow
 * 
 * Per design.md v0.2:
 * - Light-first, airy auth (force light theme)
 * - Glass card with Upwork green CTA
 * - lucide icons only (no emojis)
 */

import { useState, useEffect, FormEvent } from 'react';
import { useAuthActions } from '@convex-dev/auth/react';
import {
    Button, Card, CardContent, CardDescription, CardHeader, CardTitle,
    Logo, Search, PenTool, BarChart2, Input
} from '@uprosal/ui';
import { Mail, Eye, EyeOff, ArrowLeft } from 'lucide-react';

// Google Icon SVG component
function GoogleIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
    );
}

type AuthMode = 'signIn' | 'forgotPassword' | 'resetPassword';

export function SignInPage() {
    const { signIn } = useAuthActions();

    // Auth state
    const [mode, setMode] = useState<AuthMode>('signIn');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Force light theme for auth page per design.md
    useEffect(() => {
        const html = document.documentElement;
        const wasClassList = html.className;
        html.classList.remove('dark');

        return () => {
            if (wasClassList.includes('dark')) {
                html.classList.add('dark');
            }
        };
    }, []);

    const handleGoogleSignIn = () => {
        void signIn('google');
    };

    const handleEmailPasswordSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            if (mode === 'signIn') {
                await signIn('password', {
                    email,
                    password,
                    flow: 'signIn'
                });
            } else if (mode === 'forgotPassword') {
                await signIn('password', {
                    email,
                    flow: 'reset'
                });
                setMode('resetPassword');
            } else if (mode === 'resetPassword') {
                await signIn('password', {
                    email,
                    code: verificationCode,
                    newPassword: password,
                    flow: 'reset-verification',
                });
                setMode('signIn');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Authentication failed');
        } finally {
            setIsLoading(false);
        }
    };

    const renderAuthForm = () => {
        switch (mode) {
            case 'forgotPassword':
                return (
                    <form onSubmit={handleEmailPasswordSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Email
                            </label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full h-11"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Sending...' : 'Send Reset Code'}
                        </Button>
                        <button
                            type="button"
                            onClick={() => setMode('signIn')}
                            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground w-full justify-center"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to sign in
                        </button>
                    </form>
                );

            case 'resetPassword':
                return (
                    <form onSubmit={handleEmailPasswordSubmit} className="space-y-4">
                        <p className="text-sm text-muted-foreground text-center">
                            We sent a code to <strong>{email}</strong>
                        </p>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Verification Code
                            </label>
                            <Input
                                type="text"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value)}
                                placeholder="Enter 6-digit code"
                                className="text-center text-lg tracking-widest"
                                maxLength={8}
                                required
                            />
                        </div>
                        {mode === 'resetPassword' && (
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    New Password
                                </label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="New password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        )}
                        <Button
                            type="submit"
                            className="w-full h-11"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Verifying...' : 'Reset Password'}
                        </Button>
                    </form>
                );

            default: // signIn or signUp
                return (
                    <>
                        {/* Google OAuth button */}
                        <div className="space-y-3">
                            <Button
                                onClick={handleGoogleSignIn}
                                variant="outline"
                                className="w-full h-11"
                            >
                                <GoogleIcon className="mr-2 h-5 w-5" />
                                Continue with Google
                            </Button>
                        </div>

                        {/* Divider */}
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-border" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-card px-2 text-muted-foreground">
                                    Or continue with email
                                </span>
                            </div>
                        </div>

                        {/* Email/Password form */}
                        <form onSubmit={handleEmailPasswordSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Email
                                </label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Password
                                </label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter your password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setMode('forgotPassword')}
                                    className="text-sm text-primary hover:underline"
                                >
                                    Forgot password?
                                </button>
                            </div>
                            <Button
                                type="submit"
                                className="w-full h-11"
                                disabled={isLoading}
                            >
                                <Mail className="mr-2 h-4 w-4" />
                                {isLoading ? 'Loading...' : 'Sign In'}
                            </Button>
                        </form>
                    </>
                );
        }
    };

    const getCardTitle = () => {
        switch (mode) {
            case 'forgotPassword':
                return 'Reset your password';
            case 'resetPassword':
                return 'Enter new password';
            default:
                return 'Sign in to continue';
        }
    };

    const getCardDescription = () => {
        switch (mode) {
            case 'forgotPassword':
                return "We'll send you a code to reset your password";
            case 'resetPassword':
                return 'Enter the code and your new password';
            default:
                return 'Choose your preferred sign-in method';
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-background">
            {/* Background gradient - light, airy per design.md */}
            <div className="absolute inset-0 bg-linear-to-br from-white via-green-50/30 to-cyan-50/40" />

            {/* Subtle blur blobs */}
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl" />

            {/* Content */}
            <div className="relative z-10 w-full max-w-md space-y-6">
                {/* Logo & Tagline */}
                <div className="text-center space-y-3">
                    <div className="flex justify-center">
                        <Logo size="lg" showText={false} />
                    </div>
                    <h1 className="text-2xl font-bold text-foreground tracking-tight">
                        Uprosal
                    </h1>
                    <p className="text-muted-foreground">
                        AI-powered proposal generation for Upwork freelancers
                    </p>
                </div>

                {/* Auth Card - Glass effect per design.md */}
                <Card className="glass-card">
                    <CardHeader className="text-center pb-4">
                        <CardTitle className="text-xl">{getCardTitle()}</CardTitle>
                        <CardDescription>
                            {getCardDescription()}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {error && (
                            <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                                {error}
                            </div>
                        )}
                        {renderAuthForm()}

                        <p className="text-center text-xs text-muted-foreground mt-6">
                            By signing in, you agree to our{' '}
                            <a href="#" className="text-primary hover:underline">
                                Terms of Service
                            </a>{' '}
                            and{' '}
                            <a href="#" className="text-primary hover:underline">
                                Privacy Policy
                            </a>
                        </p>
                    </CardContent>
                </Card>

                {/* Feature tiles - lucide icons per design.md */}
                <div className="grid grid-cols-3 gap-3">
                    <FeatureTile icon={Search} label="Analyze Jobs" />
                    <FeatureTile icon={PenTool} label="Generate Proposals" />
                    <FeatureTile icon={BarChart2} label="Track Results" />
                </div>
            </div>
        </div>
    );
}

interface FeatureTileProps {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
}

function FeatureTile({ icon: Icon, label }: FeatureTileProps) {
    return (
        <Card className="glass-card text-center py-4 px-2 hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 cursor-default">
            <div className="flex justify-center mb-2">
                <Icon className="h-6 w-6 text-primary" />
            </div>
            <div className="text-xs text-muted-foreground font-medium">{label}</div>
        </Card>
    );
}
